<template>
  <div class="space-y-1">
    <template v-if="kind === 'boolean'">
      <label class="inline-flex items-center gap-2 text-sm text-slate-200">
        <input type="checkbox" class="accent-cyan-500" :checked="booleanValue" @change="emitValue($event.target.checked)">
        <span>{{ selector.label || 'Enabled' }}</span>
      </label>
    </template>

    <template v-else-if="kind === 'number'">
      <input
        type="number"
        class="ha-builder-control w-full text-sm"
        :min="selector.min"
        :max="selector.max"
        :step="selector.step ?? 'any'"
        :value="modelValue"
        @input="emitValue($event.target.value === '' ? '' : Number($event.target.value))">
    </template>

    <template v-else-if="kind === 'dynamic'">
      <select class="ha-builder-control w-full text-sm" :value="valueMode" @change="setValueMode($event.target.value)">
        <option value="literal">Literal</option>
        <option value="dynamic">Dynamic</option>
      </select>
      <select
        v-if="valueMode === 'dynamic'"
        class="ha-builder-control mt-1 w-full text-sm"
        :value="modelValue"
        @change="emitValue($event.target.value)">
        <option v-if="!dynamicOptions.length && !isSavedDynamicValue" disabled value="">No dynamic values available</option>
        <option v-for="option in dynamicOptions" :key="String(option.value)" :value="option.value">
          {{ option.label }}
        </option>
        <option v-if="isSavedDynamicValue" :value="modelValue">{{ modelValue }} (saved)</option>
      </select>
      <textarea
        v-else-if="['object', 'template'].includes(selector.literalKind)"
        class="ha-builder-control mt-1 min-h-16 w-full text-sm"
        :value="modelValue"
        @input="emitValue($event.target.value)"></textarea>
      <input
        v-else
        :type="selector.literalKind === 'number' ? 'number' : 'text'"
        class="ha-builder-control mt-1 w-full text-sm"
        :min="selector.min"
        :max="selector.max"
        :step="selector.step ?? 'any'"
        :value="modelValue"
        @input="emitLiteral($event.target.value)">
    </template>

    <template v-else-if="['template', 'object'].includes(kind)">
      <textarea
        class="ha-builder-control min-h-16 w-full text-sm"
        :value="modelValue"
        @input="emitValue($event.target.value)"></textarea>
    </template>

    <template v-else-if="['color_rgb', 'duration', 'device', 'area'].includes(kind)">
      <input
        type="text"
        class="ha-builder-control w-full text-sm"
        :placeholder="kind === 'color_rgb' ? '255, 0, 0' : placeholder"
        :value="modelValue"
        @input="emitValue($event.target.value)">
    </template>

    <template v-else-if="isSelectLike">
      <select v-if="!selectorInputFallback" class="ha-builder-control w-full text-sm" :value="modelValue" @change="emitValue($event.target.value)">
        <option value="">— select —</option>
        <option v-if="!resolvedOptions.length" disabled value="">{{ emptyOptionLabel }}</option>
        <option v-for="option in resolvedOptions" :key="String(option.value)" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <input
        v-else
        type="text"
        class="ha-builder-control w-full text-sm"
        :placeholder="placeholder || 'entity.id'"
      :value="modelValue"
        @input="emitValue($event.target.value)">
    </template>

    <select
      v-else-if="kind === 'multi_select'"
      multiple
      class="ha-builder-control w-full text-sm"
      :value="multiValue"
      @change="emitValue([...$event.target.selectedOptions].map(option => option.value))">
      <option v-for="option in resolvedOptions" :key="String(option.value)" :value="option.value">
        {{ option.label }}
      </option>
    </select>

    <input
      v-else
      type="text"
      class="ha-builder-control w-full text-sm"
      :placeholder="placeholder"
      :value="modelValue"
      @input="emitValue($event.target.value)">

    <p v-if="loading" class="text-xs text-slate-500" role="status">Loading Home Assistant metadata…</p>
    <p v-else-if="error" class="text-xs text-amber-400" role="status">Home Assistant metadata unavailable. Saved values are preserved.</p>
    <p v-else-if="metadataEmpty" class="text-xs text-slate-500" role="status">{{ emptyStatusLabel }}</p>
    <p v-if="unknownSavedValue" class="text-xs text-amber-400">Saved value is not in the current metadata and will be preserved.</p>
    <p v-if="validationMessage" class="text-xs text-red-400">{{ validationMessage }}</p>
    <p v-else-if="selector.description" class="text-xs text-slate-500">{{ selector.description }}</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { HomeAssistantMetadataAdapter } from './metadata/HomeAssistantMetadataAdapter.js'
import { normalizeSelectorOptions } from './metadata/selectorModel.js'

const props = defineProps({
  modelValue: { type: [String, Number, Boolean, Array, Object], default: '' },
  selector: { type: Object, default: () => ({ kind: 'text' }) },
  settings: { type: Object, default: null },
  placeholder: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const adapter = new HomeAssistantMetadataAdapter(props.settings)
const error = ref('')
const loading = ref(false)

const kind = computed(() => props.selector?.kind ?? 'text')
const isSelectLike = computed(() => ['entity', 'state', 'attribute', 'designer-property', 'select', 'service', 'target'].includes(kind.value))
const metadataKind = computed(() => ['entity', 'target', 'state', 'attribute', 'service'].includes(kind.value))
const booleanValue = computed(() => props.modelValue === true || props.modelValue === 1 || props.modelValue === '1' || props.modelValue === 'true')
const valueMode = computed(() => isDynamicValue(props.modelValue) ? 'dynamic' : 'literal')

const dynamicOptions = computed(() => {
  const variables = (props.selector?.dynamicVariables ?? []).map(variable => ({
    value: `{{ ${variable.name} }}`,
    label: variable.label ?? variable.name,
  }))
  return normalizeSelectorOptions([
    ...(props.selector?.options ?? []),
    ...(props.selector?.suggestions ?? []),
    ...variables,
  ])
})

const isSavedDynamicValue = computed(() => valueMode.value === 'dynamic' &&
  !dynamicOptions.value.some(option => option.value === String(props.modelValue ?? '')))

const resolvedOptions = computed(() => {
  const selector = props.selector ?? {}
  if (kind.value === 'entity' || kind.value === 'target') {
    return adapter.entityOptions({
      domain: selector.filters?.domain ?? selector.domain ?? '',
      current: String(props.modelValue ?? ''),
    })
  }
  if (kind.value === 'attribute') {
    return adapter.attributeOptions(selector.entityId, {
      current: String(props.modelValue ?? ''),
      extra: selector.options ?? [],
    })
  }
  if (kind.value === 'state') {
    return adapter.stateOptions(selector.entityId, { current: String(props.modelValue ?? '') })
  }
  if (kind.value === 'service') {
    return adapter.serviceOptions({
      recommendedDomains: selector.recommendedDomains,
      current: String(props.modelValue ?? ''),
      includeAll: selector.includeAll !== false,
    })
  }
  const options = normalizeSelectorOptions(selector.options ?? [])
  const current = String(props.modelValue ?? '')
  if (selector.preserveUnknown !== false && current && !options.some(option => option.value === current)) {
    return [{ value: current, label: `${current} (saved)` }, ...options]
  }
  return options
})

const selectorInputFallback = computed(() => {
  if (!isSelectLike.value) return false
  if (['select', 'designer-property', 'service'].includes(kind.value)) return false
  if (['entity', 'target'].includes(kind.value)) {
    return !props.settings?.tauri || Boolean(error.value)
  }
  return Boolean(error.value) || resolvedOptions.value.length === 0
})

const emptyOptionLabel = computed(() => {
  if (kind.value === 'attribute') return 'No attributes available'
  if (kind.value === 'state') return 'No states available'
  if (kind.value === 'service') return 'No services available'
  return 'No matching values'
})

const emptyStatusLabel = computed(() => {
  if (kind.value === 'service') return 'No Home Assistant actions are available.'
  if (kind.value === 'attribute') return 'No attributes are available for this entity yet.'
  if (kind.value === 'state') return 'No state metadata is available for this entity yet.'
  if (kind.value === 'entity' || kind.value === 'target') return 'No Home Assistant entities are available.'
  return 'No options are available.'
})

const unknownSavedValue = computed(() => {
  const current = String(props.modelValue ?? '')
  if (!current || !props.selector?.preserveUnknown) return false
  return resolvedOptions.value.some(option => option.value === current && option.unknown)
})

const metadataEmpty = computed(() => metadataKind.value && !loading.value && !error.value &&
  !String(props.modelValue ?? '') && resolvedOptions.value.length === 0 && Boolean(props.settings?.tauri))

const validationMessage = computed(() => {
  if (props.selector?.required && (props.modelValue == null || String(props.modelValue).trim() === '')) {
    return 'This field is required.'
  }
  if (kind.value === 'number' || (kind.value === 'dynamic' && props.selector?.literalKind === 'number')) {
    const value = props.modelValue
    if (value !== '' && value != null && Number.isNaN(Number(value))) return 'Enter a number.'
    if (props.selector?.min != null && Number(value) < Number(props.selector.min)) return `Minimum is ${props.selector.min}.`
    if (props.selector?.max != null && Number(value) > Number(props.selector.max)) return `Maximum is ${props.selector.max}.`
  }
  return ''
})

const multiValue = computed(() => {
  if (Array.isArray(props.modelValue)) return props.modelValue.map(String)
  return String(props.modelValue ?? '').split(',').map(value => value.trim()).filter(Boolean)
})

function isDynamicValue(value) {
  return typeof value === 'string' && (/{{[\s\S]*}}/.test(value) || value.startsWith('object.'))
}

function emitValue(value) {
  emit('update:modelValue', value)
}

function emitLiteral(value) {
  if (props.selector.literalKind === 'number' && value !== '') emitValue(Number(value))
  else emitValue(value)
}

function setValueMode(mode) {
  if (mode === 'dynamic') {
    const first = dynamicOptions.value[0]?.value ?? ''
    if (!isDynamicValue(props.modelValue)) emitValue(first)
    return
  }
  if (isDynamicValue(props.modelValue)) emitValue('')
}

async function loadMetadata() {
  adapter.setSettings(props.settings)
  const needsEntities = ['entity', 'target', 'state', 'attribute'].includes(kind.value)
  const needsServices = kind.value === 'service'
  if (!needsEntities && !needsServices) return
  if (!props.settings?.tauri) return
  loading.value = true
  error.value = ''
  try {
    if (needsEntities) {
      await adapter.loadEntities()
    } else {
      await adapter.loadServices()
    }
    error.value = needsEntities ? adapter.lastEntityError : adapter.lastServiceError
  } catch (loadError) {
    // The adapter retains its last successful snapshot; the current value is
    // therefore still rendered as a saved/unknown option.
    error.value = String(loadError)
  } finally {
    loading.value = false
  }
}

onMounted(loadMetadata)
watch(
  () => [kind.value, props.selector?.entityId, props.selector?.filters?.domain, props.settings?.tauri, props.settings?.hass?.host, props.settings?.hass?.port],
  loadMetadata,
)
</script>

